import React, { useContext, useEffect, useMemo, useState } from "react";
import { ProfileContext } from "../../context/ProfileContext";
import { ASSOCIATESTATUS_TYPES } from "../../functions/postActionTypes";
import {
  createAssociateSearchFilter,
  createCrewSearchFilter,
  segmentAssociateRequests,
  sortAssociatesByName,
} from "../../utils/associateFilters";
import { Crew, CrewContext } from "../../context/CrewContext";
import {
  IonSearchbar,
  IonAccordionGroup,
  IonAccordion,
  IonItem,
  IonLabel,
  IonBadge,
  IonSegment,
  IonSegmentButton,
  IonList,
  IonAvatar,
  IonButton,
  IonIcon,
  useIonAlert,
  IonText,
  IonSpinner,
} from "@ionic/react";
import { AuthContext } from "../../context/AuthContext";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { LoadingScreen } from "../../components/LoadingScreen";
import { WebLayout } from "../../components/layouts/WebLayout";
import { AssociateDetailPanel } from "../../components/desktop/AssociateDetailPanel";
import { CrewDetailPanel } from "../../components/desktop/CrewDetailPanel";
import { fx_FB_MarkAssociateRequestRead } from "../../functions/Fx_AssociateFunctions";
import { CreateCrewModal } from "../../components/crews/CreateCrewModal";
import { EditCrewMembersModal } from "../../components/crews/EditCrewMembersModal";
import { useCrewMemberEditor } from "../../hooks/useCrewMemberEditor";
import { useAssociateRequestHandlers } from "../../hooks/useAssociateRequestHandlers";
import "../../theme/AppStyles.css";
import { add, peopleOutline } from "ionicons/icons";

type AssociatesViewMode = "associates" | "crews";

const FALLBACK_AVATAR = "../assets/profilePic.png";

const getInitials = (name: string) => {
  const parts = (name || "").trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "C";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const AssociatesDesktop: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState<AssociatesViewMode>("associates");
  
  // Get associateId from URL query params
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialAssociateId = queryParams.get("active");
  const [selectedAssociateId, setSelectedAssociateId] = useState<string | null>(initialAssociateId);

  const [selectedCrew, setSelectedCrew] = useState<Crew | null>(null);
  const [createCrewOpen, setCreateCrewOpen] = useState(false);
  const [editingCrewId, setEditingCrewId] = useState<string | null>(null);
  const [newCrewName, setNewCrewName] = useState("");
  const [newCrewDescription, setNewCrewDescription] = useState("");
  const [savingCrew, setSavingCrew] = useState(false);
  const [deletingCrew, setDeletingCrew] = useState(false);
  const [pendingOpenCrewId, setPendingOpenCrewId] = useState<string | null>(null);

  const [editMembersOpen, setEditMembersOpen] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [savingMembers, setSavingMembers] = useState(false);
  const [memberSearchText, setMemberSearchText] = useState("");
  const [showSelectedOnly] = useState(false);

  const [presentAlert] = useIonAlert();

  const {
    myAssociates,
    myRequests,
    everyoneElse,
    badgeCountAssociateRequestsContext,
    profile,
  } = useContext(ProfileContext);

  const { handleSendRequest } = useAssociateRequestHandlers({
    profile,
    presentAlert,
  });

  const {
    crews,
    crewsLoading,
    createCrew,
    updateCrewMembers,
    updateCrewDetails,
    deleteCrew,
  } = useContext(CrewContext);

  const { currentUser, authReady } = useContext(AuthContext);

  const canSeeCrews = !!profile?.UserLastRoleScheduler;

  // Update selected associate when URL changes
  useEffect(() => {
    const newAssociateId = queryParams.get("active");
    if (newAssociateId !== selectedAssociateId) {
      setSelectedAssociateId(newAssociateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleAssociateSelect = async (associateId: string, isRead: boolean) => {
    setSelectedAssociateId(associateId);
    history.replace(`/Associates?active=${associateId}`);
    
    // Mark as read if needed
    if (isRead === false) {
      await fx_FB_MarkAssociateRequestRead(profile.UserId, associateId).catch(() => {});
    }
  };

  const handleAddAssociate = (
    associateUID: string,
    associatefName: string,
    associatelName: string,
    associateCompany: string,
    associatePic: string,
  ) => {
    handleSendRequest(
      associateUID,
      associatefName,
      associatelName,
      associateCompany,
      associatePic
    );
  };

  useEffect(() => {
    if (!canSeeCrews && viewMode === "crews") {
      setViewMode("associates");
    }
  }, [canSeeCrews, viewMode]);

  useEffect(() => {
    if (!pendingOpenCrewId) return;

    const found = crews.find((crew) => crew.id === pendingOpenCrewId);
    if (!found) return;

    setSelectedCrew(found);
    setPendingOpenCrewId(null);
  }, [pendingOpenCrewId, crews]);

  useEffect(() => {
    if (viewMode !== "crews" || !selectedCrew?.id) return;

    const latest = crews.find((crew) => crew.id === selectedCrew.id);
    if (!latest) {
      setSelectedCrew(null);
      return;
    }

    const changed =
      latest.memberCount !== selectedCrew.memberCount ||
      (latest.description || "") !== (selectedCrew.description || "") ||
      (latest.name || "") !== (selectedCrew.name || "");

    if (changed) {
      setSelectedCrew(latest);
    }
  }, [crews, selectedCrew, viewMode]);

  const searchTextFunction = (typedValue: string) => {
    setSearchText(typedValue);
  };

  const searchLower = useMemo(() => searchText.trim().toLowerCase(), [searchText]);

  const crewsFiltered = useMemo(() => {
    if (searchLower === "") return crews;
    const matchesCrew = createCrewSearchFilter(searchText);
    return (crews || []).filter(matchesCrew);
  }, [crews, searchLower, searchText]);

  // Use shared filter utility
  const matchesSearch = useMemo(
    () => createAssociateSearchFilter(searchText),
    [searchText]
  );

  // Base buckets using shared segmentation utility
  const { incomingRequests: incomingRequestsBase, sentRequests: sentRequestsBase } = useMemo(() => {
    return segmentAssociateRequests(
      myRequests,
      ASSOCIATESTATUS_TYPES.RequestReceived,
      ASSOCIATESTATUS_TYPES.RequestSent
    );
  }, [myRequests]);

  const associatesSortedBase = useMemo(() => {
    return sortAssociatesByName(myAssociates);
  }, [myAssociates]);

  const incomingRequests = useMemo(() => {
    return incomingRequestsBase.filter(matchesSearch);
  }, [incomingRequestsBase, matchesSearch]);

  const sentRequests = useMemo(() => {
    return sentRequestsBase.filter(matchesSearch);
  }, [sentRequestsBase, matchesSearch]);

  const associatesSorted = useMemo(() => {
    return associatesSortedBase.filter(matchesSearch);
  }, [associatesSortedBase, matchesSearch]);

  const everyoneFiltered = useMemo(() => {
    return (everyoneElse || []).filter(matchesSearch);
  }, [everyoneElse, matchesSearch]);

  const showEveryone = useMemo(() => searchLower.length > 0, [searchLower]);

  const unreadIncomingCount = badgeCountAssociateRequestsContext || 0;

  const [incomingOpen, setIncomingOpen] = useState<boolean>(unreadIncomingCount > 0);
  const [sentOpen, setSentOpen] = useState<boolean>(false);
  const [associatesOpen, setAssociatesOpen] = useState<boolean>(true);

  const didMountRef = React.useRef(false);
  const lastUnreadRef = React.useRef<number>(unreadIncomingCount);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      lastUnreadRef.current = unreadIncomingCount;
      return;
    }

    const prevUnread = lastUnreadRef.current;
    lastUnreadRef.current = unreadIncomingCount;

    if (prevUnread === 0 && unreadIncomingCount > 0) {
      setIncomingOpen(true);
    }
  }, [unreadIncomingCount]);

  const searchPlaceholder = useMemo(() => {
    return viewMode === "crews" ? "Search crews..." : undefined;
  }, [viewMode]);

  const {
    crewMembersResolved,
    memberSearchLower,
    filteredSortedMembers,
    visibleUids,
    allVisibleSelected,
    someVisibleSelected,
    visibleSelectedCount,
    toggleSelectAllVisible,
  } = useCrewMemberEditor({
    myAssociates,
    selectedCrew,
    memberSearchText,
    showSelectedOnly,
    selectedMemberIds,
    setSelectedMemberIds,
  });

  const openCreateCrew = () => {
    setEditingCrewId(null);
    setNewCrewName("");
    setNewCrewDescription("");
    setCreateCrewOpen(true);
  };

  const closeCreateCrew = () => {
    setCreateCrewOpen(false);
    setEditingCrewId(null);
  };

  const openEditCrewDetails = () => {
    if (!selectedCrew?.id) return;
    setEditingCrewId(selectedCrew.id);
    setNewCrewName(selectedCrew.name || "");
    setNewCrewDescription(selectedCrew.description || "");
    setCreateCrewOpen(true);
  };

  const handleSaveCrewDetails = async () => {
    try {
      const nameTrim = (newCrewName || "").trim();
      if (!nameTrim) {
        await presentAlert({
          header: "Crew Name Required",
          message: "Please enter a crew name.",
          buttons: ["OK"],
        });
        return;
      }

      setSavingCrew(true);

      const payload = {
        name: nameTrim,
        description: (newCrewDescription || "").trim(),
      };

      if (!editingCrewId) {
        const newId = await createCrew(payload);
        setPendingOpenCrewId(newId);
      } else {
        await updateCrewDetails({
          crewId: editingCrewId,
          name: payload.name,
          description: payload.description,
        });
      }

      setSavingCrew(false);
      setCreateCrewOpen(false);
      setEditingCrewId(null);
    } catch (err: any) {
      console.error("Save crew details error:", err);
      setSavingCrew(false);
      await presentAlert({
        header: editingCrewId ? "Couldn’t Update Crew" : "Couldn’t Create Crew",
        message: err?.message || "Unknown error",
        buttons: ["OK"],
      });
    }
  };

  const openEditMembers = () => {
    const existing = Array.isArray(selectedCrew?.memberIds) ? (selectedCrew?.memberIds as string[]) : [];
    setSelectedMemberIds(existing);
    setMemberSearchText("");
    setEditMembersOpen(true);
  };

  const closeEditMembers = () => {
    setEditMembersOpen(false);
    setMemberSearchText("");
  };

  const toggleMember = (uid: string, checked: boolean) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(uid);
      else next.delete(uid);
      return Array.from(next);
    });
  };

  const handleSaveMembers = async () => {
    try {
      if (!selectedCrew?.id) return;
      setSavingMembers(true);
      await updateCrewMembers({ crewId: selectedCrew.id, memberIds: selectedMemberIds });
      setSavingMembers(false);
      setEditMembersOpen(false);
    } catch (err: any) {
      console.error("Save members error:", err);
      setSavingMembers(false);
      await presentAlert({
        header: "Couldn’t Save Members",
        message: err?.message || "Unknown error",
        buttons: ["OK"],
      });
    }
  };

  const handleDeleteCrew = async () => {
    try {
      if (!selectedCrew?.id || savingCrew || savingMembers || deletingCrew) return;

      const crewId = selectedCrew.id;
      const crewName = selectedCrew.name || "this crew";

      await presentAlert({
        header: "Delete Crew?",
        message: `This will permanently delete "${crewName}". This action cannot be undone.`,
        buttons: [
          { text: "Cancel", role: "cancel" },
          {
            text: "Delete",
            role: "destructive",
            handler: async () => {
              try {
                setDeletingCrew(true);
                setEditMembersOpen(false);
                setCreateCrewOpen(false);
                setSelectedCrew(null);
                await deleteCrew({ crewId });
                setDeletingCrew(false);
              } catch (err: any) {
                console.error("Delete crew error:", err);
                setDeletingCrew(false);
                await presentAlert({
                  header: "Couldn’t Delete Crew",
                  message: err?.message || "Unknown error",
                  buttons: ["OK"],
                });
              }
            },
          },
        ],
      });
    } catch (err) {
      console.error("Delete crew alert error:", err);
    }
  };

  if (!authReady) {
    return <LoadingScreen LoadingText="Loading" />;
  }

  if (currentUser.uid === "") {
    return <Redirect to={{ pathname: "/login", search: "?returnUrl=/portal" }} />;
  }

  return (
    <WebLayout title="Associates">
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Segment control */}
        {canSeeCrews && (
          <div style={{ padding: "0 16px", paddingTop: "8px", flexShrink: 0 }}>
            <IonSegment
              value={viewMode}
              onIonChange={(e) => setViewMode((e.detail.value as AssociatesViewMode) || "associates")}
            >
              <IonSegmentButton
                value="associates"
              >
                <IonLabel>
                  Associates
                </IonLabel>
              </IonSegmentButton>

              <IonSegmentButton
                value="crews"
              >
                <IonLabel>
                  Crews
                </IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </div>
        )}

        {/* Search */}
        <div style={{ flexShrink: 0 }}>
          <IonSearchbar
            value={searchText}
            onIonChange={(e) => searchTextFunction(e.detail.value || "")}
            animated
            placeholder={searchPlaceholder}
          />
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', width: '100%' }}>
          {/* Left Column - Associates/Crews List */}
          <div
            style={{
              width: '50%',
              height: "100%",
              overflowY: "auto",
              padding: "0",
              borderRight: "1px solid var(--ion-color-light-shade)",
            }}
          >
            {viewMode === "associates" && (
              <>
                {/* INCOMING REQUESTS */}
                <IonAccordionGroup
                  value={incomingOpen ? "incoming" : undefined}
                  onIonChange={(e) => setIncomingOpen(!!e.detail.value)}
                >
                  <IonAccordion
                    value="incoming"
                  >
                    <IonItem
                      slot="header"
                      lines="full"
                    >
                      <IonLabel>
                        <strong>Incoming Requests</strong>
                      </IonLabel>

                      {(badgeCountAssociateRequestsContext > 0 || incomingRequestsBase.length > 0) && (
                        <IonBadge
                          slot="end"
                          color={badgeCountAssociateRequestsContext > 0 ? "danger" : "medium"}
                        >
                          {badgeCountAssociateRequestsContext > 0
                            ? badgeCountAssociateRequestsContext
                            : incomingRequestsBase.length}
                        </IonBadge>
                      )}
                    </IonItem>

                    <div slot="content" style={{ paddingTop: "6px" }}>
                      {incomingRequests.length === 0 ? (
                        <IonItem lines="none">
                          <IonLabel color="medium">
                            {searchLower ? "No matches in incoming requests." : "No incoming requests."}
                          </IonLabel>
                        </IonItem>
                      ) : (
                        <IonList>
                          {incomingRequests
                            .filter((u: any) => u.WerkerUid !== "nZhDwrvpfbRWoQz3P6VTdZR8nrp2" && u.WerkerUid !== "")
                            .map((user: any) => {
                              const isSelected = selectedAssociateId === user.WerkerUid;
                              return (
                                <IonItem
                                  key={user.WerkerUid}
                                  button
                                  onClick={() => handleAssociateSelect(user.WerkerUid, user.isRead_Request)}
                                  color={isSelected ? "light" : undefined}
                                  style={{
                                    borderLeft: isSelected
                                      ? "4px solid var(--ion-color-primary)"
                                      : "4px solid transparent",
                                    fontWeight: isSelected ? "600" : "normal",
                                  }}
                                >
                                  <IonAvatar slot="start">
                                    <img
                                      src={user.WerkerProfilePicURL || FALLBACK_AVATAR}
                                      alt=""
                                      onError={(e: any) => {
                                        e.target.src = FALLBACK_AVATAR;
                                      }}
                                    />
                                  </IonAvatar>
                                  <IonLabel>
                                    <h2 style={{ fontWeight: user.isRead_Request ? 400 : 700 }}>
                                      {user.WerkerFirstName} {user.WerkerLastName}
                                    </h2>
                                    <p>{user.WerkerCompany}</p>
                                  </IonLabel>
                                  {!user.isRead_Request && (
                                    <IonBadge slot="end" color="danger">
                                      •
                                    </IonBadge>
                                  )}
                                </IonItem>
                              );
                            })}
                        </IonList>
                      )}
                    </div>
                  </IonAccordion>
                </IonAccordionGroup>

                {/* SENT REQUESTS */}
                <IonAccordionGroup
                  value={sentOpen ? "sent" : undefined}
                  onIonChange={(e) => setSentOpen(!!e.detail.value)}
                >
                  <IonAccordion
                    value="sent"
                  >
                    <IonItem
                      slot="header"
                      lines="full"
                    >
                      <IonLabel>
                        <strong>Sent Requests</strong>
                      </IonLabel>

                      {sentRequestsBase.length > 0 && (
                        <IonBadge slot="end" color="medium">
                          {sentRequestsBase.length}
                        </IonBadge>
                      )}
                    </IonItem>

                    <div slot="content" style={{ paddingTop: "6px" }}>
                      {sentRequests.length === 0 ? (
                        <IonItem lines="none">
                          <IonLabel color="medium">
                            {searchLower ? "No matches in sent requests." : "No sent requests."}
                          </IonLabel>
                        </IonItem>
                      ) : (
                        <IonList>
                          {sentRequests
                            .filter((u: any) => u.WerkerUid !== "nZhDwrvpfbRWoQz3P6VTdZR8nrp2" && u.WerkerUid !== "")
                            .map((user: any) => {
                              const isSelected = selectedAssociateId === user.WerkerUid;
                              return (
                                <IonItem
                                  key={user.WerkerUid}
                                  button
                                  onClick={() => handleAssociateSelect(user.WerkerUid, user.isRead_Request)}
                                  color={isSelected ? "light" : undefined}
                                  style={{
                                    borderLeft: isSelected
                                      ? "4px solid var(--ion-color-primary)"
                                      : "4px solid transparent",
                                    fontWeight: isSelected ? "600" : "normal",
                                  }}
                                >
                                  <IonAvatar slot="start">
                                    <img
                                      src={user.WerkerProfilePicURL || FALLBACK_AVATAR}
                                      alt=""
                                      onError={(e: any) => {
                                        e.target.src = FALLBACK_AVATAR;
                                      }}
                                    />
                                  </IonAvatar>
                                  <IonLabel>
                                    <h2>{user.WerkerFirstName} {user.WerkerLastName}</h2>
                                    <p>{user.WerkerCompany}</p>
                                  </IonLabel>
                                </IonItem>
                              );
                            })}
                        </IonList>
                      )}
                    </div>
                  </IonAccordion>
                </IonAccordionGroup>

                {/* CONFIRMED ASSOCIATES */}
                <IonAccordionGroup
                  value={associatesOpen ? "associates" : undefined}
                  onIonChange={(e) => setAssociatesOpen(!!e.detail.value)}
                >
                  <IonAccordion
                    value="associates"
                  >
                    <IonItem
                      slot="header"
                      lines="full"
                    >
                      <IonLabel>
                        <strong>Associates</strong>
                      </IonLabel>

                      {associatesSortedBase.length > 0 && (
                        <IonBadge slot="end" color="medium">
                          {associatesSortedBase.length}
                        </IonBadge>
                      )}
                    </IonItem>

                    <div slot="content" style={{ paddingTop: "6px" }}>
                      {associatesSorted.length === 0 ? (
                        <IonItem lines="none">
                          <IonLabel color="medium">
                            {searchLower ? "No matches in associates." : "No associates yet."}
                          </IonLabel>
                        </IonItem>
                      ) : (
                        <IonList>
                          {associatesSorted
                            .filter((u: any) => u.WerkerUid !== "nZhDwrvpfbRWoQz3P6VTdZR8nrp2" && u.WerkerUid !== "")
                            .map((user: any) => {
                              const isSelected = selectedAssociateId === user.WerkerUid;
                              return (
                                <IonItem
                                  key={user.WerkerUid}
                                  button
                                  onClick={() => handleAssociateSelect(user.WerkerUid, user.isRead_Request)}
                                  color={isSelected ? "light" : undefined}
                                  style={{
                                    borderLeft: isSelected
                                      ? "4px solid var(--ion-color-primary)"
                                      : "4px solid transparent",
                                    fontWeight: isSelected ? "600" : "normal",
                                  }}
                                >
                                  <IonAvatar slot="start">
                                    <img
                                      src={user.WerkerProfilePicURL || FALLBACK_AVATAR}
                                      alt=""
                                      onError={(e: any) => {
                                        e.target.src = FALLBACK_AVATAR;
                                      }}
                                    />
                                  </IonAvatar>
                                  <IonLabel>
                                    <h2>{user.WerkerFirstName} {user.WerkerLastName}</h2>
                                    <p>{user.WerkerCompany}</p>
                                  </IonLabel>
                                </IonItem>
                              );
                            })}
                        </IonList>
                      )}
                    </div>
                  </IonAccordion>
                </IonAccordionGroup>

                {/* EVERYONE ELSE (when searching) */}
                {showEveryone && everyoneFiltered.length > 0 && (
                  <div>
                    <IonItem lines="full">
                      <IonLabel>
                        <strong>Everyone Else</strong>
                      </IonLabel>
                      <IonBadge slot="end" color="medium">
                        {everyoneFiltered.length}
                      </IonBadge>
                    </IonItem>

                    <IonList>
                      {everyoneFiltered
                        .filter((u: any) => u.WerkerUid !== "nZhDwrvpfbRWoQz3P6VTdZR8nrp2" && u.WerkerUid !== "")
                        .map((user: any) => {
                          const isSelected = selectedAssociateId === user.WerkerUid;
                          return (
                            <IonItem
                              key={user.WerkerUid}
                              button
                              onClick={() => handleAssociateSelect(user.WerkerUid, user.isRead_Request || true)}
                              color={isSelected ? "light" : undefined}
                              style={{
                                borderLeft: isSelected ? "4px solid var(--ion-color-primary)" : "4px solid transparent",
                                fontWeight: isSelected ? "600" : "normal",
                              }}
                            >
                              <IonAvatar slot="start">
                                <img
                                  src={user.WerkerProfilePicURL || FALLBACK_AVATAR}
                                  alt=""
                                  onError={(e: any) => {
                                    e.target.src = FALLBACK_AVATAR;
                                  }}
                                />
                              </IonAvatar>
                              <IonLabel>
                                <h2>{user.WerkerFirstName} {user.WerkerLastName}</h2>
                                <p>{user.WerkerCompany}</p>
                              </IonLabel>
                              <IonButton
                                slot="end"
                                size="small"
                                color="secondwarning"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleAddAssociate(
                                    user.WerkerUid,
                                    user.WerkerFirstName,
                                    user.WerkerLastName,
                                    user.WerkerCompany,
                                    user.WerkerProfilePicURL || FALLBACK_AVATAR,
                                  );
                                }}
                              >
                                Add Associate
                              </IonButton>
                            </IonItem>
                          );
                        })}
                    </IonList>
                  </div>
                )}
              </>
            )}

            {/* CREWS VIEW */}
            {viewMode === "crews" && (
              <div style={{ padding: "16px 0" }}>
                <IonItem lines="none" style={{ marginBottom: "8px" }}>
                  <IonButton slot="end" color="secondWarning" onClick={openCreateCrew}>
                    Create Crew
                    <IonIcon icon={add} slot="end" />
                  </IonButton>
                </IonItem>

                {crewsLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "32px" }}>
                    <IonSpinner />
                  </div>
                ) : crewsFiltered.length === 0 ? (
                  <div style={{ padding: "32px", textAlign: "center" }}>
                    <IonIcon
                      icon={peopleOutline}
                      style={{ fontSize: "64px", color: "var(--ion-color-medium)", marginBottom: "16px" }}
                    />
                    <h2 style={{ marginBottom: "8px" }}>{searchLower ? "No Matching Crews" : "No Crews Yet"}</h2>
                    <IonText color="medium">
                      <p style={{ margin: 0 }}>
                        {searchLower ? "Try a different search." : "Create your first crew to start grouping associates."}
                      </p>
                    </IonText>
                  </div>
                ) : (
                  <IonList>
                    {crewsFiltered.map((crew) => {
                      const isSelected = selectedCrew?.id === crew.id;
                      return (
                        <IonItem
                          key={crew.id}
                          button
                          onClick={() => setSelectedCrew(crew)}
                          color={isSelected ? "light" : undefined}
                          style={{
                            borderLeft: isSelected
                              ? "4px solid var(--ion-color-primary)"
                              : "4px solid transparent",
                            fontWeight: isSelected ? "600" : "normal",
                          }}
                        >
                          <IonAvatar slot="start">
                            {crew.avatarUrl ? (
                              <img src={crew.avatarUrl} alt={crew.name} />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 800,
                                  fontSize: "14px",
                                  background: "#e5e5e5",
                                  color: "#333",
                                }}
                              >
                                {getInitials(crew.name)}
                              </div>
                            )}
                          </IonAvatar>
                          <IonLabel className="ion-text-wrap">
                            <h2>{crew.name}</h2>
                            <p>{crew.description && crew.description.trim().length > 0 ? crew.description : "No description"}</p>
                          </IonLabel>
                          <IonBadge slot="end" color="medium">
                            {crew.memberCount}
                          </IonBadge>
                        </IonItem>
                      );
                    })}
                  </IonList>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Associate or Crew Detail Panel */}
          <div
            style={{
              width: '50%',
              height: "100%",
              padding: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {viewMode === "crews" ? (
              <CrewDetailPanel
                selectedCrew={selectedCrew}
                crewMembersResolved={crewMembersResolved}
                getInitials={getInitials}
                onEditCrewDetails={openEditCrewDetails}
                onOpenEditMembers={openEditMembers}
                onDeleteCrew={handleDeleteCrew}
                deletingCrew={deletingCrew}
                savingCrew={savingCrew}
                savingMembers={savingMembers}
                loading={crewsLoading}
              />
            ) : !selectedAssociateId ? (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  opacity: 0.85,
                }}
              >
                <img
                  src="../assets/GorillaJonesNoBackground.png"
                  alt="WerkApe"
                  style={{ width: "70%", maxWidth: 480, height: "auto", objectFit: "contain" }}
                />
                <p style={{ color: "var(--ion-color-medium)", fontSize: 14, margin: 0 }}>
                  Select an associate to view details
                </p>
              </div>
            ) : (
              <AssociateDetailPanel associateId={selectedAssociateId} />
            )}
          </div>
        </div>

        <CreateCrewModal
          isOpen={createCrewOpen}
          onDidDismiss={closeCreateCrew}
          editingCrewId={editingCrewId}
          newCrewName={newCrewName}
          setNewCrewName={setNewCrewName}
          newCrewDescription={newCrewDescription}
          setNewCrewDescription={setNewCrewDescription}
          onSave={handleSaveCrewDetails}
          savingCrew={savingCrew}
        />

        <EditCrewMembersModal
          isOpen={editMembersOpen}
          onDidDismiss={closeEditMembers}
          onSave={handleSaveMembers}
          savingMembers={savingMembers}
          memberSearchText={memberSearchText}
          setMemberSearchText={setMemberSearchText}
          memberSearchLower={memberSearchLower}
          toggleSelectAllVisible={toggleSelectAllVisible}
          allVisibleSelected={allVisibleSelected}
          someVisibleSelected={someVisibleSelected}
          visibleSelectedCount={visibleSelectedCount}
          visibleUidsCount={visibleUids.length}
          filteredSortedMembers={filteredSortedMembers}
          selectedMemberIds={selectedMemberIds}
          toggleMember={toggleMember}
        />
      </div>
    </WebLayout>
  );
};

export default AssociatesDesktop;
